import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useTheme } from "../../../contexts/ThemeContext";
import * as d3 from "d3";
import LargeModal from "../../../ui/LargeModal";
import LabelText from "../../../ui/LabelText";
import EmptyDataButton from "../../../ui/EmptyDataButton";
import InfoIcon from "../../../assets/icons/infoicon.svg";

const StyledChartWrapper = styled.div`
  & .chart-wrapper {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
  }
  & .chart-container {
    width: 1020px;
    margin-left: -15px;
    position: relative;
    height: max-content;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  & .legend-container {
    display: flex;
    gap: 15px;
    align-items: center;
    flex-wrap: wrap;
    flex-direction: column;
    align-items: baseline;
  }
  & .legend-container .legend {
    margin: 0px;
  }
  & .legend {
    text-transform: uppercase;
    font-size: 15px;
    font-family: "DM Mono", monospace;
    font-style: normal;
    display: flex;
    gap: 5px;
    align-items: center;
  }
`;

interface BarChartData {
  name: string;
  percent: number;
  color: string;
}

interface FinancialBarchartProps {
  data: any[];
}

const FinancialBarchart: React.FC<FinancialBarchartProps> = ({ data }) => {
  const emptyData = data.every((item) => item.value === null || item.value === 0);
  const { theme } = useTheme();
  const chartRef = useRef<SVGSVGElement>(null);
  const [isLargeModalOpen, setLargeModalIsOpen] = useState(false);
  const [isInfoModal, setIsInfoModal] = useState<boolean>(false);

  const toggleLargeModal = () => {
    setLargeModalIsOpen(!isLargeModalOpen);
  };

  const modalData = {
    headerTitle: "Data not provided by club",
    actionTag: " ",
    items: [
      {
        subTitle: " ",
        description:
          "Leeds United are situated in a country who’s laws to not require them to make public the data we’d need to fully calculate this sum.",
      },
    ],
  };

  const InfoData = {
    headerTitle: "Player types",
    actionTag: " ",
    items: [
      {
        subTitle: "Regular starters",
        description:
          "\nPlayers who are currently starting 70% or more matches are considered regular starters.\n\nRegular starters who also score highly in terms of match contribution are considered Core players in the Squad structure graph below.\n\n",
      },
      {
        subTitle: "Squad & fringe players",
        description:
          "\nSquad players are those who currently feature in 35% - 70% of their club team’s matches.\n\n Any player who features in less than 35% of their club team’s matches is considered a fringe player.\n",
      },
    ],
  };

  function drawText(
    group: d3.Selection<SVGGElement, any, any, any>,
    x: number,
    y: number,
    dy: string,
    fill: string,
    fontFamily: string,
    fontSize: string,
    fontWeight: string,
    text: string
  ): void {
    group
      .append("text")
      .attr("x", x)
      .attr("y", y)
      .attr("dy", dy)
      .style("fill", fill)
      .style("font-family", fontFamily)
      .style("font-size", fontSize)
      .style("font-weight", fontWeight)
      .text(text);
  }

  useEffect(() => {
    const resizeChart = () => {
      const container = document.getElementById("chart-container");

      if (chartRef.current && container && data?.length > 0) {
        const containerWidth = container.clientWidth;
        const margin = { top: 0, right: 20, bottom: 0, left: 60 };
        const width = containerWidth - margin.left - margin.right;
        const height = 320;
        const barHeight = 40;
        const maxData = d3.max(data, (d) => d.value) || 100;
        const maxItem = data.find((d) => d.value === maxData || maxData === 100);
        let remainingData = data.filter((d) => d !== maxItem);
        if (!maxItem) return;
        const svg = d3
          .select(chartRef.current)
          .attr("width", "100%")
          .attr("height", "100%")
          .attr("viewBox", `0 0 ${containerWidth} ${height}`)
          .attr("preserveAspectRatio", "xMidYMid meet");
        const isCostHighest = maxItem.type === "cost";
        let mainBarData: any[] = [];
        let stackedBarData: any[] = [];
        stackedBarData = data.filter((d) => d.type !== "revenue");
        if (isCostHighest) {
          mainBarData = [data.find((d) => d.type === "cost")!];
          stackedBarData = data.filter((d) => d.type !== "cost");
          const netProfitData = stackedBarData.find((d) => d.type === "profit");
          if (netProfitData && netProfitData.value < 0) {
            stackedBarData = stackedBarData.map((d) => {
              if (d.type === "profit") {
                return {
                  ...d,
                  value: Math.abs(d.value),
                  name: d.name === "Equity" ? "Equity" : "Net Loss",
                };
              }
              return d;
            });
          }
        }
        const x = d3
          .scaleLinear()
          .range([margin.left, width - margin.right])
          .domain([0, maxData]);
        const y = d3
          .scaleBand()
          .range([margin.top, height - margin.bottom])
          .domain([maxItem.name, "Stacked Values"])
          .padding(3);
        d3.select(chartRef.current).selectAll("*").remove();
        if (maxData !== 100) {
          svg
            .append("line")
            .attr("x1", x(0) - 50)
            .attr("y1", isCostHighest ? height - 270 : height - 300)
            .attr("x2", x(0) - 50)
            .attr("y2", isCostHighest ? 310 : 270)
            .attr("stroke", theme.playerCardBorderColor)
            .attr("stroke-width", 1);
        }
        if (maxData !== 100) {
          svg
            .selectAll(".fixed-shape")
            .data(data)
            .enter()
            .append("path")
            .attr("d", (d) => {
              const shapeBarHeight = 50;
              const startX = x(0) - 40; // Positioning the shape 30px to the left of the bar
              const endX = x(0);
              const curveHeight = 20;
              const yPos = isCostHighest ? 160 : 70;
              return `
            M ${startX},${yPos + shapeBarHeight - curveHeight}
            C ${startX},${yPos + shapeBarHeight} ${startX},${yPos + shapeBarHeight + curveHeight} ${endX},${
                yPos + shapeBarHeight + curveHeight
              }
            L ${endX},${yPos + shapeBarHeight - curveHeight}
            Z
          `;
            })
            .attr("fill", (d) => {
              return d.type === "cost" ? "#00D1A8" : isCostHighest ? "#FF7900" : "#4AF075";
            });
          {
          }
          svg
            .selectAll(".bar")
            .data([maxItem])
            .enter()
            .append("path")
            .attr("class", "bar")
            .attr("d", (d, i) => {
              const barX = x(0);
              const barY = isCostHighest ? 190 : 100;
              const barWidth = x(d.value) - x(0) + 5;
              const scaledRadius = 20;
              return `M${barX},${barY}
            Q${barX},${barY} ${barX + scaledRadius},${barY}
            H${barX + barWidth - scaledRadius}
            Q${barX + barWidth},${barY} ${barX + barWidth},${barY + scaledRadius}
            V${barY + barHeight - scaledRadius}
            Q${barX + barWidth},${barY + barHeight} ${barX + barWidth - scaledRadius},${barY + barHeight}
            H${barX + scaledRadius}
            Q${barX},${barY + barHeight} ${barX},${barY + barHeight}
            Z`;
            })
            .attr("fill", (d) => d.color);
        }
        const barTextGroup = svg.selectAll(".bar-text").data([maxItem]).enter().append("g").attr("class", "bar-text-group");
        if (maxData !== 100) {
          drawText(
            barTextGroup,
            x(0) - 35,
            y(maxItem.name)! - (isCostHighest ? -120 : 120),
            ".35em",
            theme.playerColor,
            "Aeonik",
            "20px",
            "700",
            maxItem.name
          );
          const barIconTextGroup = svg.selectAll(".bar-icon-text").data([maxItem]).enter().append("g").attr("class", "bar-icon-text-group");
          drawText(
            barIconTextGroup,
            x(0) - 32,
            y(maxItem.name)! - (isCostHighest ? -153 : 90),
            ".35em",
            theme.NumberColor,
            "DM Mono",
            "24px",
            "500",
            "€"
          );
          const barValueTextGroup = svg
            .selectAll(".bar-value-text")
            .data([maxItem])
            .enter()
            .append("g")
            .attr("class", "bar-value-text-group");
          drawText(
            barValueTextGroup,
            x(0) - 15,
            y(maxItem.name)! - (isCostHighest ? -150 : 90),
            ".35em",
            theme.playerColor,
            "DM Mono",
            "32px",
            "500",
            maxItem.valueText
          );
          const barSeasonTextGroup = svg
            .selectAll(".bar-season-text")
            .data([maxItem])
            .enter()
            .append("g")
            .attr("class", "bar-season-text-group");
          drawText(
            barSeasonTextGroup,
            x(0) - 30,
            y(maxItem.name)! + (isCostHighest ? 175 : -60),
            ".35em",
            theme.NumberColor,
            "DM Mono",
            "12px",
            "500",
            "SEASON"
          );
        }
        svg
          .selectAll(".stacked-bar-group")
          .data(stackedBarData)
          .enter()
          .append("g")
          .attr("class", "stacked-bar-group")
          .each(function (d, i) {
            const barX = x(d3.sum(stackedBarData.slice(0, i), (d) => d.value)) + i * 8;
            const barY = y("Stacked Values")! - 38;
            const barWidth = x(d.value) - x(0);
            const firstDataPointValue = stackedBarData[0].value;
            const tempbarWidth = x(firstDataPointValue) - x(0);
            const scaledRadius = d.value > 0 ? 18 : 0;
            const fillColor = i === 1 && isCostHighest ? "#FFC18A" : d.color;
            if (maxData !== 100) {
              if (i === 0) {
                const shapeBarHeight = 50;
                const startX = barX - 40; // Positioning the shape 30px to the left of the bar
                const endX = barX;
                const curveHeight = 20;
                const yPos = barY + y.bandwidth() / 1.69 - shapeBarHeight / 1.69;
                d3.select(this)
                  .append("path")
                  .attr(
                    "d",
                    `
                  M ${startX},${yPos + shapeBarHeight - curveHeight}
                  C ${startX},${yPos + shapeBarHeight} ${startX},${yPos + shapeBarHeight + curveHeight} ${endX},${
                      yPos + shapeBarHeight + curveHeight
                    }
                  L ${endX},${yPos + shapeBarHeight - curveHeight}
                  Z
                `
                  )
                  .attr("fill", d.color);
              }
            }
            d3.select(this)
              .append("path")
              .attr("class", "stacked-bar")
              .attr(
                "d",
                i === stackedBarData.length - 1
                  ? `
              M${barX},${barY}
              h${barWidth - scaledRadius}
              a${scaledRadius},${scaledRadius} 0 0 1 ${scaledRadius},${scaledRadius}
              v${barHeight - scaledRadius * 2}
              a${scaledRadius},${scaledRadius} 0 0 1 -${scaledRadius},${scaledRadius}
              h-${barWidth - scaledRadius}
              v-${barHeight}
              z
            `
                  : `
              M${barX},${barY}
              h${barWidth}
              v${barHeight}
              h-${barWidth}
              z
            `
              )
              .attr("fill", fillColor);
            if (maxData !== 100) {
              if (i === 0) {
                d3.select(this)
                  .append("text")
                  .attr(
                    "x",
                    (d: any) => x(d3.sum(remainingData.slice(0, i), (d) => (d.value === 0 || d.value === null ? 15 : d.value))) - 36
                  )
                  .attr("y", (d: any) => y("Stacked Values")! + barHeight + (isCostHighest ? -165 : -10))
                  .attr("dy", ".35em") // Center text vertically
                  .style("font-family", "Aeonik")
                  .style("font-size", "20px")
                  .style("width", "100px")
                  .style("font-weight", 700)
                  .style("word-break", "break-all")
                  .style("fill", theme.playerColor)
                  .text((d: any) => d.name);
                d3.select(this)
                  .append("text")
                  .attr("x", (d: any) => x(d3.sum(remainingData.slice(0, i), (d) => d.value)) - 32)
                  .attr("y", (d: any) => y("Stacked Values")! + barHeight + (isCostHighest ? -135 : 20))
                  .attr("dy", ".35em")
                  .attr("fill", theme.NumberColor)
                  .style("font-family", "DM Mono")
                  .style("font-size", "24px")
                  .style("font-weight", 500)
                  .text("€");
                d3.select(this)
                  .append("text")
                  .attr("x", (d: any) => x(d3.sum(remainingData.slice(0, i), (d) => d.value)) - 15)
                  .attr("y", (d: any) => y("Stacked Values")! + barHeight + (isCostHighest ? -135 : 20))
                  .attr("dy", ".35em") // Center text vertically
                  .style("fill", theme.playerColor)
                  .style("font-family", "DM Mono")
                  .style("font-size", "32px")
                  .style("font-weight", 500)
                  .text((d: any) => d.valueText);
                d3.select(this)
                  .append("text")
                  .attr("x", (d: any) => x(d3.sum(remainingData.slice(0, i), (d) => d.value)) - 30)
                  .attr("y", (d: any) => y("Stacked Values")! + barHeight + (isCostHighest ? -105 : 45))
                  .attr("dy", ".35em") // Center text vertically
                  .style("fill", theme.NumberColor)
                  .style("font-family", "DM Mono")
                  .style("font-size", "12PX")
                  .style("font-weight", 500)
                  .text("SEASON");
                if (d.name === "Regular starters") {
                  setIsInfoModal(true);
                  const iconX = x(d3.sum(remainingData.slice(0, i), (d) => d.value)) - -117; // Adjust the X position as needed
                  const iconY = y("Stacked Values")! + barHeight + (isCostHighest ? -167 : -23); // Adjust the Y position as needed
                  svg
                    .append("image")
                    .attr("x", iconX) // Center the icon horizontally
                    .attr("y", iconY) // Center the icon vertically
                    .attr("width", 24)
                    .attr("height", 24)
                    .attr("href", InfoIcon) // The image URL or path
                    .style("cursor", "pointer")
                    .on("click", () => {
                      setLargeModalIsOpen(!isLargeModalOpen);
                    });
                }
              } else {
                d3.select(this)
                  .append("text")
                  .attr(
                    "x",
                    (d: any) =>
                      x(d3.sum(remainingData.slice(0, i), (d) => (d.value === 0 || d.value === null ? 15 : d.value))) +
                      (tempbarWidth < 140 ? 110 : 27)
                  )
                  .attr("y", (d: any) => y("Stacked Values")! + barHeight + (isCostHighest ? -165 : -10))
                  .attr("dy", ".35em") // Center text vertically
                  .style("font-family", "Aeonik")
                  .style("font-size", "20px")
                  .style("width", "100px")
                  .style("font-weight", 700)
                  .style("word-break", "break-all")
                  .style("fill", theme.playerColor)
                  .text((d: any) => d.name);
                d3.select(this)
                  .append("text")
                  .attr("x", (d: any) => x(d3.sum(remainingData.slice(0, i), (d) => d.value)) + (tempbarWidth < 140 ? 110 : 27))
                  .attr("y", (d: any) => y("Stacked Values")! + barHeight + (isCostHighest ? -135 : 20))
                  .attr("dy", ".35em")
                  .attr("fill", theme.NumberColor)
                  .style("font-family", "DM Mono")
                  .style("font-size", "24px")
                  .style("font-weight", 500)
                  .text("€");
                d3.select(this)
                  .append("text")
                  .attr("x", (d: any) => x(d3.sum(remainingData.slice(0, i), (d) => d.value)) + (tempbarWidth < 140 ? 130 : 47))
                  .attr("y", (d: any) => y("Stacked Values")! + barHeight + (isCostHighest ? -135 : 20))
                  .attr("dy", ".35em") // Center text vertically
                  .style("fill", theme.playerColor)
                  .style("font-family", "DM Mono")
                  .style("font-size", "32px")
                  .style("font-weight", 500)
                  .text((d: any) => d.valueText);
                d3.select(this)
                  .append("text")
                  .attr("x", (d: any) => x(d3.sum(remainingData.slice(0, i), (d) => d.value)) + (tempbarWidth < 140 ? 110 : 27))
                  .attr("y", (d: any) => y("Stacked Values")! + barHeight + (isCostHighest ? -105 : 45))
                  .attr("dy", ".35em") // Center text vertically
                  .style("fill", theme.NumberColor)
                  .style("font-family", "DM Mono")
                  .style("font-size", "12PX")
                  .style("font-weight", 500)
                  .text("SEASON");
                if (d.name === "Squad & fringe") {
                  setIsInfoModal(true);
                  const iconX = x(d3.sum(remainingData.slice(0, i), (d) => d.value)) + (tempbarWidth < 140 ? 255 : 173); // Adjust the X position as needed
                  const iconY = y("Stacked Values")! + barHeight + (isCostHighest ? -157 : -23); // Adjust the Y position as needed
                  svg
                    .append("image")
                    .attr("x", iconX) // Center the icon horizontally
                    .attr("y", iconY) // Center the icon vertically
                    .attr("width", 24)
                    .attr("height", 24)
                    .attr("href", InfoIcon) // The image URL or path
                    .style("cursor", "pointer")
                    .on("click", () => {
                      setLargeModalIsOpen(!isLargeModalOpen);
                    });
                }
              }
              remainingData.forEach((d, i) => {
                if (i > 0) {
                  const barX = x(d3.sum(stackedBarData.slice(0, i), (d) => d.value)) + i * 4;
                  const barY = y("Stacked Values")!;
                  const lineHeight = barHeight;
                  if (tempbarWidth <= 140 && !isCostHighest) {
                    svg
                      .append("line")
                      .attr("x1", barX)
                      .attr("y1", barY + (isCostHighest ? -120 : -38))
                      .attr("x2", barX)
                      .attr("y2", barY + lineHeight - (isCostHighest ? 40 : 35))
                      .attr("stroke", theme.playerCardBorderColor)
                      .attr("stroke-width", 1);
                    svg
                      .append("line")
                      .attr("x1", barX + 80)
                      .attr("y1", barY + (isCostHighest ? -130 : 6))
                      .attr("x2", barX + 80)
                      .attr("y2", barY + lineHeight - (isCostHighest ? 40 : -45))
                      .attr("stroke", theme.playerCardBorderColor)
                      .attr("stroke-width", 1);
                    svg
                      .append("line")
                      .attr("x1", barX + 0)
                      .attr("y1", barY + 6)
                      .attr("x2", barX + 80)
                      .attr("y2", barY + 6)
                      .attr("stroke", theme.playerCardBorderColor)
                      .attr("stroke-width", 1);
                  }
                  else if (tempbarWidth <= 140 && isCostHighest) {
                    svg
                      .append("line")
                      .attr("x1", barX)
                      .attr("y1", barY + (isCostHighest ? -42 : -38))
                      .attr("x2", barX)
                      .attr("y2", barY + lineHeight - (isCostHighest ? 40 : 35))
                      .attr("stroke", theme.playerCardBorderColor)
                      .attr("stroke-width", 1);
                    svg
                      .append("line")
                      .attr("x1", barX + 100)
                      .attr("y1", barY + (isCostHighest ? -135 : 6))
                      .attr("x2", barX + 100)
                      .attr("y2", barY + lineHeight - (isCostHighest ? 82 : -45))
                      .attr("stroke", theme.playerCardBorderColor)
                      .attr("stroke-width", 1);
                    svg
                      .append("line")
                      .attr("x1", barX + 0)
                      .attr("y1", barY - 42)
                      .attr("x2", barX + 100)
                      .attr("y2", barY - 42)
                      .attr("stroke", theme.playerCardBorderColor)
                      .attr("stroke-width", 1);
                  } else {
                    svg
                      .append("line")
                      .attr("x1", barX)
                      .attr("y1", barY + (isCostHighest ? -120 : -38))
                      .attr("x2", barX)
                      .attr("y2", barY + lineHeight - (isCostHighest ? 40 : -45))
                      .attr("stroke", theme.playerCardBorderColor)
                      .attr("stroke-width", 1);
                  }
                }
              });
            }
          });
      }
    };
    resizeChart();
    window.addEventListener("resize", resizeChart);
    return () => {
      window.removeEventListener("resize", resizeChart);
    };
  }, [data, theme]);

  return (
    <>
      <LargeModal toggleLargeModal={toggleLargeModal} isOpen={isLargeModalOpen} data={isInfoModal ? InfoData : modalData} />
      <StyledChartWrapper id="chart-wrapper" className="padding-inner">
        {emptyData && (
          <>
            <div className="empty-data-container">
              <div className="empty-data-content">
                <div>
                  <LabelText className="player-search-text">{data[0]?.name}</LabelText>
                </div>
                <EmptyDataButton label="Data not provided by club" />
              </div>
              <div className="empty-data-content">
                <div>
                  <LabelText className="player-search-text">{data[1]?.name}</LabelText>
                  <span>
                    <LabelText className="player-search-text"> / </LabelText>
                  </span>
                  <LabelText className="player-search-text">{data[2]?.name}</LabelText>
                </div>
                <EmptyDataButton label="Data not provided by club" />
              </div>
            </div>
            <LabelText></LabelText>
          </>
        )}
        {!emptyData && (
          <>
            <div className="chart-wrapper">
              <div id="chart-container" className="chart-container">
                <svg ref={chartRef}></svg>
              </div>
            </div>
          </>
        )}
      </StyledChartWrapper>
    </>
  );
};

export default FinancialBarchart;
